import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Settings as SettingsIcon, Trash2 } from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { lengthError } from '../../lib/fieldLength'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { useAuthStore } from '../../stores/authStore'
import { departmentList } from '../../lib/types'

/** Picker value + wire sentinel for the "All departments" option — the
 *  tenant-wide fallback catalogue (departmentId null) that applies to every
 *  department without its own. Must match the backend's `ALL_DEPARTMENTS`
 *  in work-report-settings.service.js. */
const ALL_DEPARTMENTS = '__all__'

/**
 * Per-tenant Work Report Settings page — Manager + Company Admin only.
 *
 * Lets a Manager configure the values that appear in the Task Type
 * and Item Status dropdowns on the Daily / Weekly / Monthly report
 * forms. Defaults are seeded automatically on first read so the page
 * never opens empty; the Manager can then add / rename / remove
 * entries to match the team's own taxonomy.
 *
 * Backend: GET / PUT `/work-report-settings` (see
 * `flowdesk-api/src/modules/work-report-settings/`).
 *
 * The `value` field is the persisted slug (used in saved report
 * rows) and shouldn't be edited freely — renaming a value would
 * orphan every historical row referencing the old slug. The UI
 * therefore exposes `label` as freely editable and auto-derives
 * `value` from the label on add. Edits to an existing row only
 * touch `label`; the original `value` stays stable.
 */

function slugify(text) {
  return String(text ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50)
}

function OptionList({ title, helpText, rows, onChange }) {
  // Deleting an option asks first (QA: the trash icon removed the row
  // with no warning). The removal still only PERSISTS on Save Changes —
  // the dialog prevents the accidental click, Save remains the commit.
  const [confirmIdx, setConfirmIdx] = useState(null)
  const norm = (s) => (s ?? '').trim().toLowerCase()
  // Per-row flag: true when this row repeats an earlier non-empty label.
  // Only the SECOND (and later) occurrence is flagged so the original
  // stays clean and the user fixes the copy they just added.
  const isDuplicate = rows.map((r, i) => {
    const key = norm(r.label)
    if (!key) return false
    return rows.findIndex((o) => norm(o.label) === key) !== i
  })

  function updateLabel(index, nextLabel) {
    const next = rows.map((r, i) => (i === index ? { ...r, label: nextLabel } : r))
    onChange(next)
  }
  function remove(index) {
    onChange(rows.filter((_, i) => i !== index))
  }
  function add() {
    onChange([...rows, { value: '', label: '' }])
  }

  return (
    <section className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-[color:var(--color-ink-strong)] m-0">
          {title}
        </h2>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          {helpText}
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic mb-3">
          No options yet — add at least one so employees have something to pick.
        </p>
      ) : (
        <ul className="space-y-2 mb-3">
          {rows.map((row, idx) => {
            // A row is invalid if its label is blank OR repeats an earlier
            // option — both block the save, so flag the field inline.
            const labelEmpty = (row.label ?? '').trim().length === 0
            const labelLenErr = lengthError(row.label, undefined, 50, 'Option label')
            const invalid = labelEmpty || isDuplicate[idx] || Boolean(labelLenErr)
            return (
              <li key={idx} className="flex items-start gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) => updateLabel(idx, e.target.value)}
                    placeholder="Option label"
                    aria-invalid={invalid}
                    className={
                      'w-full h-9 px-3 rounded-md border bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none ' +
                      (invalid
                        ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                        : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
                    }
                  />
                  {isDuplicate[idx] ? (
                    <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                      This option already exists.
                    </p>
                  ) : labelLenErr ? (
                    <p role="alert" className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
                      {labelLenErr}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // A blank row carries nothing — remove silently. A
                    // named option asks first so a stray click can't
                    // wipe it (QA).
                    if ((row.label ?? '').trim().length === 0) {
                      remove(idx)
                    } else {
                      setConfirmIdx(idx)
                    }
                  }}
                  aria-label={`Remove ${row.label || 'option'}`}
                  title="Remove option"
                  className="mt-0.5 p-2 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-dashed border-[color:var(--color-rule)] text-sm text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
      >
        <Plus size={14} />
        Add Option
      </button>

      <ConfirmDialog
        open={confirmIdx != null}
        onCancel={() => setConfirmIdx(null)}
        onConfirm={() => {
          remove(confirmIdx)
          setConfirmIdx(null)
        }}
        title="Remove This Option?"
        message={`"${(rows[confirmIdx]?.label ?? '').trim()}" will be removed from the ${title} dropdown when you save. Rows on existing reports keep their saved value.`}
        confirmLabel="Remove Option"
      />
    </section>
  )
}

export function ReportSettingsPage() {
  const qc = useQueryClient()
  // Settings are per-department. A department-scoped Manager / Team Leader (and
  // an external) always edits THEIR OWN department — the backend auto-scopes.
  // A WORKSPACE-WIDE reports member may edit ANY department, so they get a
  // department picker (below); the chosen id is passed to the load + save,
  // which the backend already honours (resolveDepartmentId + assertCanEdit).
  const ownDepartmentName = useAuthStore((s) => s.user?.departmentName) ?? null
  const canPickDepartment = useAuthStore((s) =>
    s.hasOperation('reports.view_workspace'),
  )
  // Report Settings is SELF-ONLY (user decision): the per-department picker is
  // never shown. A company-wide member (or Company Admin / Sub Admin) edits ONLY
  // the company-wide ("All departments") fallback catalogue; a department-scoped
  // member auto-edits their own department. No one configures another
  // department's catalogue from here, so `selectedDeptId` stays ALL_DEPARTMENTS
  // and a workspace-wide viewer's load + save target the company-wide fallback.
  const showDepartmentPicker = false
  // [External Collaboration] Externals CAN configure their GRANTED department's
  // Work Report catalogue — the backend scopes both load and save to it, so the
  // page is open to any member and no redirect is needed.
  const [taskTypes, setTaskTypes] = useState([])
  const [itemStatuses, setItemStatuses] = useState([])

  // Which department this wide-scope editor is configuring. Defaults to their
  // own; non-wide-scope editors ignore it (they always edit their own).
  // Default to "All departments" (the tenant-wide fallback) — matches the
  // Team Reports filter's default and is the natural landing for a
  // workspace-wide editor; they pick a specific department to override it.
  const [selectedDeptId, setSelectedDeptId] = useState(ALL_DEPARTMENTS)

  // Departments for the picker — fetched only for wide-scope editors. TaskMagix
  // is single-Workspace, so the sole workspace's departments ARE the tenant's.
  const workspacesQuery = useQuery({
    queryKey: ['workspaces', 'mine'],
    queryFn: async () => {
      const res = await api.get('/workspaces/mine')
      return res.data?.data ?? res.data ?? []
    },
    enabled: showDepartmentPicker,
  })
  const soleWorkspaceId = workspacesQuery.data?.[0]?.id ?? null
  const departmentsQuery = useQuery({
    queryKey: ['departments', soleWorkspaceId],
    queryFn: async () => {
      const res = await api.get(`/workspaces/${soleWorkspaceId}/departments`)
      return departmentList.parse(res.data).data
    },
    enabled: showDepartmentPicker && Boolean(soleWorkspaceId),
  })
  const departments = departmentsQuery.data ?? []

  // Only wide-scope editors steer the target department; everyone else lets the
  // backend auto-scope to their own (send nothing), preserving prior behaviour.
  const targetDeptId = canPickDepartment ? selectedDeptId : null

  // Name shown in the subtitle. "All departments" → null so the subtitle falls
  // back to the company-wide-fallback wording; a specific pick shows its name.
  const selectedDeptName =
    !canPickDepartment
      ? ownDepartmentName
      : selectedDeptId === ALL_DEPARTMENTS
        ? null
        : (departments.find((d) => d.id === selectedDeptId)?.name ??
          ownDepartmentName)

  const settingsQuery = useQuery({
    queryKey: ['work-report-settings', targetDeptId ?? 'own'],
    queryFn: async () => {
      const params = targetDeptId ? { departmentId: targetDeptId } : {}
      const res = await api.get('/work-report-settings', { params })
      return res.data?.data ?? res.data
    },
  })

  // Hydrate local state from the server payload on first load (and
  // after any successful save). Using effect rather than initial
  // state because the query is async; the rows are mutable inline
  // so each input doesn't need its own controlled-state wiring.
  useEffect(() => {
    const data = settingsQuery.data
    if (!data) return
    setTaskTypes(
      Array.isArray(data.taskTypeOptions)
        ? data.taskTypeOptions.map((o) => ({ value: o.value, label: o.label }))
        : [],
    )
    setItemStatuses(
      Array.isArray(data.itemStatusOptions)
        ? data.itemStatusOptions.map((o) => ({ value: o.value, label: o.label }))
        : [],
    )
  }, [settingsQuery.data])

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Materialise `value` from `label` for any new row that lacks
      // one. Stable rows keep their original `value` so historical
      // report entries referencing that slug stay valid. Drops rows
      // whose label is empty after trimming.
      const finalize = (rows) =>
        rows
          .map((r) => {
            const label = (r.label ?? '').trim()
            if (!label) return null
            const value = (r.value ?? '').trim() || slugify(label)
            return { value: value.slice(0, 50), label: label.slice(0, 50) }
          })
          .filter(Boolean)
      const body = {
        taskTypeOptions: finalize(taskTypes),
        itemStatusOptions: finalize(itemStatuses),
        // Wide-scope editors save to the picked department; others omit it so
        // the backend keeps auto-scoping to their own.
        ...(targetDeptId ? { departmentId: targetDeptId } : {}),
      }
      const res = await api.put('/work-report-settings', body)
      return res.data?.data ?? res.data
    },
    onSuccess: () => {
      toast.success('Work Report settings saved successfully.')
      void qc.invalidateQueries({ queryKey: ['work-report-settings'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Block saving when any option label is blank instead of silently
  // dropping it (the old behaviour showed a success toast even though the
  // empty row was discarded). The Manager gets a clear message and the
  // blank row stays on screen so they can fix or remove it.
  function handleSave() {
    const hasBlank = (rows) =>
      rows.some((r) => (r.label ?? '').trim().length === 0)
    if (hasBlank(taskTypes) || hasBlank(itemStatuses)) {
      toast.error(
        'Option name cannot be empty. Fill it in or remove the blank option before saving.',
      )
      return
    }
    // Reject duplicate labels within a list (case-insensitive) — two
    // identical options are meaningless in a dropdown and would slugify to
    // the same stored value.
    const firstDuplicate = (rows) => {
      const seen = new Set()
      for (const r of rows) {
        const key = (r.label ?? '').trim().toLowerCase()
        if (!key) continue
        if (seen.has(key)) return (r.label ?? '').trim()
        seen.add(key)
      }
      return null
    }
    const dup = firstDuplicate(taskTypes) || firstDuplicate(itemStatuses)
    if (dup) {
      toast.error(`"${dup}" already exists. Each option must have a unique name.`)
      return
    }
    saveMutation.mutate()
  }

  return (
    <div>
      <Breadcrumb current="Report Settings" homePath="/app" homeLabel="Dashboard" />

      {/* Stacks below `sm` so the action button is never pushed off the right edge on a phone. */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <SettingsIcon
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Report Settings
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {selectedDeptName
              ? `Set the Task Type and Item Status options for the ${selectedDeptName} department's reports.`
              : 'Set the dropdown options on reports. Each department’s Manager configures their own Task Type and Item Status; this is the company-wide fallback.'}
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending || settingsQuery.isLoading}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {/* Department picker — workspace-wide reports members only, and HIDDEN for
       *  Company Admin / Sub Admin (they manage only the single company-wide
       *  catalogue — selectedDeptId stays "All departments"). Department-scoped
       *  Managers / Team Leaders never see this: they edit their own, loaded
       *  automatically. */}
      {showDepartmentPicker ? (
        <div className="mb-6 flex items-center gap-2">
          <label
            htmlFor="report-settings-department"
            className="text-sm font-medium text-[color:var(--color-ink)]"
          >
            Department
          </label>
          <select
            id="report-settings-department"
            value={selectedDeptId}
            onChange={(e) => setSelectedDeptId(e.target.value)}
            disabled={departmentsQuery.isLoading}
            className="rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-1.5 text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer"
          >
            {/* Tenant-wide fallback catalogue — the default for every
             *  department without its own. */}
            <option value={ALL_DEPARTMENTS}>All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            {selectedDeptId === ALL_DEPARTMENTS
              ? "The default options for every department that hasn't set its own."
              : "You're configuring this department's report options."}
          </span>
        </div>
      ) : null}

      {settingsQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Loading settings…
        </p>
      ) : settingsQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(settingsQuery.error).message}
        </p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl">
          <OptionList
            title="Task Type"
            helpText="Used on every report row's Type dropdown. Each row in the Work Details table picks one of these."
            rows={taskTypes}
            onChange={setTaskTypes}
          />
          <OptionList
            title="Item Status"
            helpText="Used on every report row's Status dropdown. Reflects how far each task has progressed for that period."
            rows={itemStatuses}
            onChange={setItemStatuses}
          />
        </div>
      )}
    </div>
  )
}
